/**
 * Générateur — Il développe (par simple et double distributivités), factorise,
 * réduit des expressions algébriques simples.
 * Attendu ID : 209 | Niveau : 3ème | Domaine : Calcul littéral (d12, O9)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A7S2-A7S3). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « simple »     — simple distributivité, combinaisons k( ) ± k'( )
 *   « table »      — la table 2×2 de la double distributivité (échafaudage)
 *   « double »     — batterie (ax + b)(cx + d), carrés (az + b)²
 *   « facteur »    — faire apparaître un facteur commun (numérique / binôme)
 *   « factoriser » — factoriser (E)(F1) ± (E)(F2) et réduire le crochet
 *
 * Coefficients ENTIERS (les décimaux du doc sont écartés : litiges de saisie),
 * trous = coefficients isolés (structure fixe, leçon 4e).
 */

import {
    randomChoice, randInt, tex,
    makeTexteTrou,
} from '../../../utils';
import { fmtBinome, mulBinomes, pgcd, carre } from './litteral';

const ATTENDU_ID = 209;

// Contrainte d'écriture des saisies (FORMATS_TANOED §0.1) : dite en phrase,
// pas entre parenthèses, et une seule fois pour tout le générateur.
const CONSIGNE_SIGNE = ' Écris les coefficients avec leur signe.';

const L_SIMPLE = 'Développer par simple distributivité';
const L_TABLE = 'Développer avec la table de multiplication';
const L_DOUBLE = 'Développer par double distributivité';
const L_FACTEUR = 'Faire apparaître un facteur commun';
const L_FACTORISER = 'Factoriser par un facteur commun binôme';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const coefNonNul = (lo, hi) => { let c = 0; while (c === 0) c = randInt(lo, hi); return c; };
const LETTRES = ['x', 'y', 't', 'u', 'v', 'z'];

// ═════════ « simple » — k(ax + b), kx(ax + b), combinaisons ═════════

const genSimpleTrous = () => {
    const L = randomChoice(LETTRES);
    const forme = randomChoice(['k', 'kx', 'combi']);
    let gauche; let phrase; let params;
    if (forme === 'k') {
        const k = coefNonNul(-9, 9); const a = coefNonNul(2, 9); const b = coefNonNul(-9, 9);
        gauche = `${k}${fmtBinome(a, b, L)}`;
        phrase = `$A = {{${k * a}}}${L} + {{${k * b}}}$`;
        params = { forme, k, a, b };
    } else if (forme === 'kx') {
        const k = coefNonNul(-8, 8); const a = coefNonNul(2, 8); const b = coefNonNul(-9, 9);
        gauche = `${k}${L}${fmtBinome(a, b, L)}`;
        phrase = `$A = {{${k * a}}}${carre(L)} + {{${k * b}}}${L}$`;
        params = { forme, k, a, b };
    } else {
        // k1(a1x + b1) ± k2(a2x + b2), résultat kx + c non dégénéré.
        let k1; let a1; let b1; let k2; let a2; let b2; let signe; let K; let C;
        for (let essai = 0; essai < 60; essai++) {
            k1 = randInt(2, 8); a1 = coefNonNul(2, 9); b1 = coefNonNul(-9, 9);
            k2 = randInt(2, 8); a2 = coefNonNul(2, 9); b2 = coefNonNul(-9, 9);
            signe = randomChoice(['+', '-']);
            K = signe === '+' ? k1 * a1 + k2 * a2 : k1 * a1 - k2 * a2;
            C = signe === '+' ? k1 * b1 + k2 * b2 : k1 * b1 - k2 * b2;
            if (K !== 0 && C !== 0) break;
            K = 0;
        }
        if (!K) { k1 = 3; a1 = 4; b1 = 7; k2 = 4; a2 = 2; b2 = -9; signe = '+'; K = 20; C = -15; } // repli (doc C)
        gauche = `${k1}${fmtBinome(a1, b1, L)} ${signe === '+' ? '+' : '-'} ${k2}${fmtBinome(a2, b2, L)}`;
        phrase = `$A = {{${K}}}${L} + {{${C}}}$`;
        params = { forme, k1, a1, b1, k2, a2, b2, signe };
    }
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`A = ${gauche}`)}.</p>`
        + `<p><b>Développe et réduis.</b>${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_SIMPLE), mode: 'saisie' });
    payload._meta.params = params;
    return payload;
};

// ═════════ « table » — la table 2×2 (échafaudage) ═════════

const genTableTrous = () => {
    const L = randomChoice(LETTRES);
    const a = coefNonNul(-6, 6); const b = coefNonNul(-9, 9);
    const c = coefNonNul(-6, 6); const d = coefNonNul(-9, 9);
    const [A, B, C] = mulBinomes(a, b, c, d);
    if (B === 0) return genTableTrous.repli(L);
    const phrase = `$\\text{Cases : } {{${a * c}}}${carre(L)} \\quad {{${a * d}}}${L} \\quad {{${b * c}}}${L} \\quad {{${b * d}}}`
        + ` \\qquad \\text{Forme réduite : } {{${A}}}${carre(L)} + {{${B}}}${L} + {{${C}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On développe ${tex(`G = ${fmtBinome(a, b, L)}${fmtBinome(c, d, L)}`)} avec une table de `
        + 'multiplication 2 × 2 : un terme du premier facteur par ligne, un terme du second par colonne.</p>'
        + '<p><b>Complète les quatre cases dans l\'ordre de lecture de la table, puis la forme '
        + `réduite.</b>${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_TABLE), mode: 'saisie' });
    payload._meta.params = { a, b, c, d };
    return payload;
};
genTableTrous.repli = (L) => {
    // (2x − 3)(x + 4) = 2x² + 5x − 12 (le G du doc, lettre variable)
    const phrase = `$\\text{Cases : } {{2}}${carre(L)} \\quad {{8}}${L} \\quad {{-3}}${L} \\quad {{-12}}`
        + ` \\qquad \\text{Forme réduite : } {{2}}${carre(L)} + {{5}}${L} + {{-12}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On développe ${tex(`G = \\left(2${L} - 3\\right)\\left(${L} + 4\\right)`)} avec une table de multiplication 2 × 2.</p>`
        + `<p><b>Complète les quatre cases, puis la forme réduite.</b>${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_TABLE), mode: 'saisie' });
    payload._meta.params = { repli: true };
    return payload;
};

// ═════════ « double » — batterie + carrés ═════════

const genDoubleTrous = () => {
    const L = randomChoice(LETTRES);
    if (randInt(0, 2) === 2) {
        // Carré (az + b)² = a²z² + 2abz + b²
        const a = coefNonNul(2, 6); const b = coefNonNul(-9, 9);
        const phrase = `$N = {{${a * a}}}${carre(L)} + {{${2 * a * b}}}${L} + {{${b * b}}}$`;
        const payload = makeTexteTrou(phrase,
            `<p>On pose ${tex(`N = ${fmtBinome(a, b, L)}^{2}`)}.</p>`
            + `<p><b>Développe et réduis.</b>${CONSIGNE_SIGNE}</p>`,
            { ...opts(L_DOUBLE), mode: 'saisie' });
        payload._meta.params = { carre: true, a, b };
        return payload;
    }
    let a; let b; let c; let d; let A; let B; let C;
    for (let essai = 0; essai < 60; essai++) {
        a = coefNonNul(-6, 6); b = coefNonNul(-9, 9); c = coefNonNul(-6, 6); d = coefNonNul(-9, 9);
        [A, B, C] = mulBinomes(a, b, c, d);
        if (B !== 0) break;
        B = 0;
    }
    if (B === 0) { a = 2; b = 5; c = 3; d = 7; [A, B, C] = mulBinomes(a, b, c, d); } // repli (doc Ex.4 E)
    const phrase = `$E = {{${A}}}${carre(L)} + {{${B}}}${L} + {{${C}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`E = ${fmtBinome(a, b, L)}${fmtBinome(c, d, L)}`)}.</p>`
        + `<p><b>Développe et réduis.</b>${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_DOUBLE), mode: 'saisie' });
    payload._meta.params = { a, b, c, d };
    return payload;
};

// ═════════ « facteur » — faire apparaître le facteur commun ═════════

const genFacteurTrous = () => {
    const L = randomChoice(LETTRES);
    if (randInt(0, 1)) {
        // Numérique : Ky + Kq = K(py + q), p et q premiers entre eux, K > 1.
        let K; let p; let q;
        for (let essai = 0; essai < 60; essai++) {
            K = randInt(2, 12); p = randInt(2, 9); q = randInt(2, 12);
            if (pgcd(p, q) === 1 && p !== q) break;
            K = 0;
        }
        if (!K) { K = 3; p = 3; q = 4; }                   // repli (doc 9y + 12)
        const phrase = `$${K * p}${L} + ${K * q} = {{${K}}}\\left({{${p}}}${L} + {{${q}}}\\right)$`;
        const payload = makeTexteTrou(phrase,
            '<p><b>Fais apparaître le plus grand facteur commun possible, puis factorise.</b></p>',
            { ...opts(L_FACTEUR), mode: 'saisie' });
        payload._meta.params = { num: true, K, p, q };
        return payload;
    }
    // Binôme : (x + a)² + m(x + a) = (x + a)(x + a + m) — |m| ≥ 2 (pas de « 1( ) »)
    let a; let m;
    do {
        a = coefNonNul(-9, 9); m = coefNonNul(-9, 9);
    } while (Math.abs(m) < 2 || a + m === 0);
    const binome = fmtBinome(1, a, L);
    const phrase = `$D = \\left(${L} + {{${a}}}\\right)\\left(${L} + {{${a + m}}}\\right)$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`D = ${binome}^{2} ${m < 0 ? '-' : '+'} ${Math.abs(m)}${binome}`)}.</p>`
        + '<p><b>Factorise par le facteur commun, puis réduis le second facteur.</b> '
        + 'Écris les valeurs avec leur signe.</p>',
        { ...opts(L_FACTEUR), mode: 'saisie' });
    payload._meta.params = { num: false, a, m };
    return payload;
};

// ═════════ « factoriser » — (E)(F1) ± (E)(F2) ═════════

const genFactoriserTrous = () => {
    const L = randomChoice(LETTRES);
    let p; let q; let f1a; let f1b; let f2a; let f2b; let signe; let A; let B;
    for (let essai = 0; essai < 60; essai++) {
        p = coefNonNul(2, 6); q = coefNonNul(-9, 9);
        f1a = coefNonNul(-6, 6); f1b = coefNonNul(-9, 9);
        f2a = coefNonNul(-6, 6); f2b = coefNonNul(-9, 9);
        signe = randomChoice(['+', '-']);
        A = signe === '+' ? f1a + f2a : f1a - f2a;
        B = signe === '+' ? f1b + f2b : f1b - f2b;
        if (A !== 0 && B !== 0) break;
        A = 0;
    }
    if (!A) { p = 2; q = 1; f1a = 1; f1b = -3; f2a = 3; f2b = 2; signe = '+'; A = 4; B = -1; } // repli
    const E = fmtBinome(p, q, L);
    const gauche = `${E}${fmtBinome(f1a, f1b, L)} ${signe === '+' ? '+' : '-'} ${E}${fmtBinome(f2a, f2b, L)}`;
    const phrase = `$F = ${E}\\left({{${A}}}${L} + {{${B}}}\\right)$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`F = ${gauche}`)}.</p>`
        + `<p><b>Factorise par le facteur commun ${tex(E)}, puis réduis le second `
        + `facteur.</b>${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_FACTORISER), mode: 'saisie' });
    payload._meta.params = { p, q, f1a, f1b, f2a, f2b, signe, A, B };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    simple_texte_trou_saisie: genSimpleTrous,
    table_texte_trou_saisie: genTableTrous,
    double_texte_trou_saisie: genDoubleTrous,
    facteur_texte_trou_saisie: genFacteurTrous,
    factoriser_texte_trou_saisie: genFactoriserTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'simple', label: L_SIMPLE, etapes: 2, formatsList: buildFormats('simple') },
    { id: 'table', label: L_TABLE, etapes: 2, formatsList: buildFormats('table') },
    { id: 'double', label: L_DOUBLE, etapes: 3, formatsList: buildFormats('double') },
    { id: 'facteur', label: L_FACTEUR, etapes: 2, formatsList: buildFormats('facteur') },
    { id: 'factoriser', label: L_FACTORISER, etapes: 2, formatsList: buildFormats('factoriser') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
